import { PriceBoardFilter } from "@/components/common/section-components/filters/PriceBoardFilter";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { TabComponent } from "@/components/tabs/TabComponent";
import { PermissionCode } from "@/constants/permissions";
import { AppRoutes } from "@/constants/routes";
import { useRoles } from "@/hooks/useRoles";
import { Flex } from "@chakra-ui/react";
import {
  BadgeDollarSign,
  Banknote,
  ListFilter,
  Logs,
  ShoppingBag,
} from "lucide-react";
import { useEffect, useState } from "react";
import { FieldValues, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { PricesTable } from "./sections/PricesTable";
import { SuppliesTable } from "./sections/SuppliesTable";
import { DollarTable } from "./sections/DollarTable";
import { BoardTable } from "./sections/BoardTable";
import { GeneralFilterSection } from "./sections/GeneralFilterSection";
import {
  clearFiltersHandler,
  fetchGFSectionHandler,
  fetchSectionHandler,
  submitFiltersHandlerPricesSection,
} from "./schema/handler-functions";

import {
  BoardData,
  DollarData,
  GeneralFilterData,
  PricesData,
  SuppliesData,
} from "./schema/price-board.schema";
import { isEmpty } from "@/utils/query-string";
import { usePolling } from "@/hooks/usePolling";

export const PriceBoardPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);
  const navigate = useNavigate();

  const { userPermissions } = useRoles();

  const handleAddAssignation = () => {
    navigate(AppRoutes.PRICE_BOARD_ADD);
  };

  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);
  const [openFilters, setOpenFilters] = useState<boolean>(false);

  const { control, getValues, setValue, handleSubmit } = useForm();
  const [loadingPrices, setLoadingPrices] = useState<boolean>(false);

  const [pricesData, setPricesData] = useState<PricesData | undefined>(
    undefined,
  );
  const [suppliesData, setSuppliesData] = useState<SuppliesData | undefined>(
    undefined,
  );
  const [dollarData, setDollarData] = useState<DollarData | undefined>(
    undefined,
  );
  const [boardData, setBoardData] = useState<BoardData | undefined>(undefined);
  const [gfData, setGFData] = useState<GeneralFilterData | undefined>(
    undefined,
  );

  const [currentFilters, setCurrentFilters] = useState<any>({});
  const [currentTab, setCurrentTab] = useState<string>("Precios");

  const submitFilters = async (event: FieldValues) => {
    submitFiltersHandlerPricesSection({
      event,
      fetchSection,
      setFiltersApplied,
      setOpenFilters,
      currentSection: currentTab,
      setCurrentFilters,
    });
  };

  const clearFilters = async () => {
    clearFiltersHandler({
      getValues,
      setFiltersApplied,
      setValue,
      submitFilters,
      currentSection: currentTab,
      setCurrentFilters,
    });
  };

  usePolling({
    callback: () => {
      if (currentTab === "Filtro general") {
        return fetchGeneralFilterSection(false);
      }
      return fetchSection(currentTab, { ...currentFilters }, false);
    },
    delay: 10000,
    isActive,
  });

  useEffect(() => {
    if (!isActive) return;

    setFiltersApplied(false);
    setOpenFilters(false);
    setCurrentFilters({});
    for (const key of Object.keys(getValues())) {
      setValue(key as any, undefined);
    }

    if (currentTab === "Filtro general") return;
    fetchSection(currentTab);
  }, [currentTab, isActive]);

  const { fetchPermissions } = useRoles();

  const fetchSection = async (
    section: string,
    filters?: any,
    showLoad = true,
  ) => {
    await fetchSectionHandler({
      setLoadingPrices,
      setPricesData,
      setSuppliesData,
      setDollarData,
      setBoardData,
      filters,
      section,
      showLoad,
      navigate,
      fetchPermissions,
    });
  };

  const filterDate = useWatch({
    control,
    name: "generalDate",
  });

  const fetchGeneralFilterSection = async (showLoad = true) => {
    await fetchGFSectionHandler({
      setLoadingPrices,
      setGFData,
      filterDate,
      showLoad,
      navigate,
      fetchPermissions,
    });
  };

  useEffect(() => {
    if (!isActive) return;

    if (currentTab === "Filtro general" && !isEmpty(filterDate)) {
      fetchGeneralFilterSection();
    }
  }, [isActive, currentTab, filterDate]);

  const hasWriteAccess = Boolean(
    userPermissions?.some(
      (p) => p.codigo === PermissionCode.MARKET_CONFIG && p.tipoPermiso === "W",
    ),
  );

  return (
    <Flex
      flexDir="column"
      paddingTop="20px"
      gap="30px"
      data-testid="price-board-page"
    >
      <SectionHeader
        title="Mercado del día"
        addButtonLabel="Agregar precios"
        disableAdd={!hasWriteAccess}
        handleAdd={handleAddAssignation}
        showButtons={hasWriteAccess}
      />
      <TabComponent
        onValueChange={(value) => setCurrentTab(value.value)}
        tabs={[
          {
            label: "Precios",
            icon: <BadgeDollarSign />,
            value: "Precios",
            content: (
              <PricesTable
                loadingPrices={loadingPrices}
                sectionData={pricesData}
              />
            ),
          },
          {
            label: "Insumos",
            icon: <ShoppingBag />,
            value: "Insumos",
            content: (
              <SuppliesTable
                loadingPrices={loadingPrices}
                sectionData={suppliesData}
              />
            ),
          },
          {
            label: "Dolar Divisa",
            icon: <Banknote />,
            value: "Dolar Divisa",
            content: (
              <DollarTable
                loadingPrices={loadingPrices}
                sectionData={dollarData}
              />
            ),
          },
          {
            label: "Pizarra",
            icon: <Logs />,
            value: "Pizarra",
            content: (
              <BoardTable
                loadingPrices={loadingPrices}
                sectionData={boardData}
              />
            ),
          },
          {
            label: "Filtro general",
            icon: <ListFilter />,
            value: "Filtro general",
            content: (
              <GeneralFilterSection
                loadingPrices={loadingPrices}
                sectionData={gfData}
                control={control}
                setValue={setValue}
              />
            ),
          },
        ]}
        additionalButtons={
          currentTab === "Filtro general" ? null : (
            <PriceBoardFilter
              filtersApplied={filtersApplied}
              openFilters={openFilters}
              control={control}
              setOpenFilters={setOpenFilters}
              clearFilters={clearFilters}
              handleSubmit={handleSubmit}
              submitFilters={submitFilters}
            />
          )
        }
      />
    </Flex>
  );
};
